import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2 } from 'lucide-react'
import { useApi } from '../../utils/api'
import { useMessage } from '../../contexts/MessageContext'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { PageTitleBar } from '../../components/PageTitleBar'
import { ListTable } from '../../components/ListTable'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog'

interface ContentGroup {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

interface ApiResponse {
  data: ContentGroup[];
  success: boolean;
}

export default function ContentGroupsList() {
  const { message, setMessage } = useMessage();
  const navigate = useNavigate();
  const { fetchWithAuth } = useApi();
  const [contentGroups, setContentGroups] = useState<ContentGroup[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns = [
    { header: 'Title', accessor: 'title' },
    { header: 'Created At', accessor: 'created_at' },
    { header: 'Updated At', accessor: 'updated_at' },
  ];

  const handleEditGroup = (id: number) => {
    navigate(`/edit-content-group/${id}`);
  };

  const handleDeleteGroup = (id: number) => {
    setGroupToDelete(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(`/admin/content-groups/${groupToDelete}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete content group');
      }
      setContentGroups(groups => groups.filter(group => group.id !== groupToDelete));
      setMessage('Content group successfully deleted!');
      closeDeleteModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchContentGroups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth('/admin/content-groups');
      if (!response.ok) {
        throw new Error('Failed to fetch content groups');
      }
      const data: ApiResponse = await response.json();
      setContentGroups(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchContentGroups();
  }, [fetchContentGroups]);

  const filteredGroups = contentGroups.filter(group =>
    group.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {message && (
        <div className="mb-4 p-4 bg-[#8bcfc2] border border-[#3eaf99] text-[#0b677c] rounded">
          {message}
        </div>
      )}

      <PageTitleBar
        title="Content Groups"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Content Groups' },
        ]}
        actionLabel="New Content Group"
        actionHref="/new-content-group"
      />

      <div className="bg-white rounded-sm shadow-md">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search content groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border-gray-300 focus:border-[#3eaf99] focus:ring focus:ring-[#3eaf99] focus:ring-opacity-50"
            />
          </div>
        </div>

        <ListTable
          columns={columns}
          data={filteredGroups}
          editPathPrefix="/edit-content-group"
          rowActions={(group) => (
            <div className="flex space-x-4">
              <button
                onClick={() => handleEditGroup(group.id)}
                className="text-xs font-semibold uppercase text-gray-600 hover:text-gray-900"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteGroup(group.id)}
                className="text-xs font-semibold uppercase text-gray-600 hover:text-gray-900"
              >
                Delete
              </button>
            </div>
          )}
        />
      </div>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white rounded-md shadow-lg p-6 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">Delete content group?</DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm text-gray-500">This action cannot be undone.</p>
          <DialogFooter className="mt-6 flex justify-end space-x-2">
            <Button
              variant="ghost"
              onClick={closeDeleteModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={confirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
            >
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 